import {ICryptoService} from "./crypto.service.interface";
import {NullableString, NullableUndefinedString} from "../../../common/types/nullable-types";


export class CryptoService implements ICryptoService {

    constructor() {
        this._enkKey = this._base64ToArrayBuffer('bPuEEtkTNc9G/6b0Lzu8cOQpbdpfgWT4FRB/ltG2I8E=');
    }

    private readonly _enkKey: ArrayBufferLike;

    async decryptMessage(message: string): Promise<string> {
        const encryptedDataWithIV = this._base64ToArrayBuffer(message);
        const encryptedDataWithIvArray = new Uint8Array(encryptedDataWithIV);

        const iv = encryptedDataWithIvArray.slice(0, 16);
        const encryptedData = encryptedDataWithIvArray.slice(16);

        const importedKey = await this._createImportedKey("decrypt");

        const decryptedArrayBuffer = await crypto.subtle.decrypt(
            {
                name: "AES-CBC",
                iv: iv
            },
            importedKey,
            encryptedData
        );

        return new TextDecoder().decode(decryptedArrayBuffer);
    }

    async encryptMessage(message: string): Promise<string> {
        const importedKey = await this._createImportedKey("encrypt");
        const iv = crypto.getRandomValues(new Uint8Array(16));
        const encodedText = new TextEncoder().encode(message);
        const encrypted = await crypto.subtle.encrypt(
            { name: 'AES-CBC', iv: iv },
            importedKey,
            encodedText
        );

        // Concatenate IV and encrypted data
        const buffer = new Uint8Array(iv.length + encrypted.byteLength);
        buffer.set(iv);
        buffer.set(new Uint8Array(encrypted), iv.length);

        return this._arrayBufferToBase64(buffer);
    }



    async encryptJson<T extends object>(message: T | null): Promise<NullableString> {
        if(!message) {
            return null;
        }

        return await this.encryptMessage(JSON.stringify(message));
    }
    async decryptJson<T extends object>(message: NullableUndefinedString): Promise<T | null> {
        if(!message) {
            return null;
        }

        const decryptedMessage = await this.decryptMessage(message);
        return JSON.parse(decryptedMessage);
    }

    private async _createImportedKey(keyUsage: KeyUsage) {
        return await crypto.subtle.importKey(
            "raw",
            this._enkKey,
            { name: 'AES-CBC', length: 256 },
            false,
            [keyUsage]
        );
    }
    private _base64ToArrayBuffer(base64: string) {
        const binaryString = window.atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }


    private _arrayBufferToBase64(arrayBuffer: Iterable<number>) {
        const binary = [];
        const bytes = new Uint8Array(arrayBuffer);
        for (let i = 0; i < bytes.byteLength; i++) {
            binary.push(String.fromCharCode(bytes[i]));
        }
        return window.btoa(binary.join(''));
    }

}
